import { z } from 'zod';
import {
  experimentKeySchema as canonicalExperimentKeySchema,
  flagKeySchema,
  variantKeySchema,
} from '@galapagos/product-analytics';
import { EXPERIMENT_VERSION_MAX_LENGTH, EXPERIMENT_VERSION_PATTERN } from './version-pattern';

const SAFE_KEY = /^[a-z][a-z0-9_]*(?:[./-][a-z0-9_]+)*$/;
const SAFE_HUMAN_TEXT = /^[A-Za-z0-9][A-Za-z0-9 .,:;'"!?()/%+-]{3,399}$/;
const SAFE_AGENT_DEFINITION_ID = /^[a-z][a-z0-9]*(?:[-_][a-z0-9]+)*$/;
const RAW_PROVIDER_AGENT_ID = /^agent_[a-z0-9]{12,}$/i;
const UNSAFE_TEXT =
  /(@|\b\d{3}[-. ]?\d{3}[-. ]?\d{4}\b|(?:^|[^a-z0-9])(?:condition|substance|alcohol|opioid|tobacco|cannabis|gambling|vaping|smoking|drinking)(?:$|[^a-z0-9]))/i;

function safeString(name: string, pattern = SAFE_KEY, maxLength = 120): z.ZodType<string> {
  return z
    .string()
    .min(1)
    .max(maxLength)
    .regex(pattern, `${name} must be a safe identifier`)
    .refine((value) => !UNSAFE_TEXT.test(value), `${name} must not contain PHI or condition data`);
}

function safeAgentDefinitionId(name: string): z.ZodType<string> {
  return safeString(name, SAFE_AGENT_DEFINITION_ID).refine(
    (value) => !RAW_PROVIDER_AGENT_ID.test(value),
    `${name} must be canonical, not a raw provider agent id`,
  );
}

function safeHumanText(name: string): z.ZodType<string> {
  return z
    .string()
    .min(4)
    .max(400)
    .regex(SAFE_HUMAN_TEXT, `${name} must be concise safe text`)
    .refine((value) => !UNSAFE_TEXT.test(value), `${name} must not contain PHI or condition data`);
}

export const experimentKeySchema = canonicalExperimentKeySchema.transform((value): string => value);
export const experimentFlagKeySchema = flagKeySchema.transform((value): string => value);
export const experimentVersionSchema = safeString(
  'version',
  EXPERIMENT_VERSION_PATTERN,
  EXPERIMENT_VERSION_MAX_LENGTH,
);
export const experimentVariantKeySchema = variantKeySchema.transform((value): string => value);
export const experimentAssignmentSourceSchema = z.enum(['galapagos', 'posthog']);
export const experimentSubjectKeySchema = safeString(
  'subject_key',
  /^(?:subject|test|qa)_[a-z0-9:_-]{2,160}$/,
);

export const experimentStatusSchema = z.enum([
  'draft',
  'ready',
  'running',
  'paused',
  'decided',
  'archived',
]);
export const experimentTargetScopeSchema = z.enum([
  'agent_definition',
  'agent_screen_element',
  'first_message',
  'genui_flow',
  'genui_step',
  'genui_element',
  'genui_surface',
  'genui_surface_element',
]);
export const experimentTargetSetPolicySchema = z.enum(['same-scope', 'identical']);

const commonTargetFields = {
  artifact_version: experimentVersionSchema.optional(),
};

export const agentDefinitionTargetSchema = z
  .object({
    ...commonTargetFields,
    scope: z.literal('agent_definition'),
    agent_definition_id: safeAgentDefinitionId('agent_definition_id'),
    agent_definition_version: experimentVersionSchema,
  })
  .strict();

export const agentScreenElementTargetSchema = z
  .object({
    ...commonTargetFields,
    scope: z.literal('agent_screen_element'),
    agent_definition_id: safeAgentDefinitionId('agent_definition_id'),
    agent_definition_version: experimentVersionSchema,
    agent_screen_id: safeString('agent_screen_id'),
    agent_screen_version: experimentVersionSchema,
    agent_screen_element_id: safeString('agent_screen_element_id'),
  })
  .strict();

/**
 * A first-message target varies only the opening line of a served agent
 * definition: the definition itself is unchanged, and `first_message_pool`
 * names the `first_message_variants` arm that variant plays.
 */
export const firstMessageTargetSchema = z
  .object({
    ...commonTargetFields,
    scope: z.literal('first_message'),
    agent_definition_id: safeAgentDefinitionId('agent_definition_id'),
    agent_definition_version: experimentVersionSchema,
    first_message_pool: safeString('first_message_pool'),
  })
  .strict();

export const genuiFlowTargetSchema = z
  .object({
    ...commonTargetFields,
    scope: z.literal('genui_flow'),
    flow_id: safeString('flow_id'),
    flow_version: experimentVersionSchema,
  })
  .strict();

export const genuiStepTargetSchema = z
  .object({
    ...commonTargetFields,
    scope: z.literal('genui_step'),
    flow_id: safeString('flow_id'),
    flow_version: experimentVersionSchema,
    step_key: safeString('step_key'),
    screen_id: safeString('screen_id'),
  })
  .strict();

export const genuiElementTargetSchema = z
  .object({
    ...commonTargetFields,
    scope: z.literal('genui_element'),
    flow_id: safeString('flow_id'),
    flow_version: experimentVersionSchema,
    step_key: safeString('step_key'),
    screen_id: safeString('screen_id'),
    element_key: safeString('element_key'),
  })
  .strict();

export const genuiSurfaceTargetSchema = z
  .object({
    ...commonTargetFields,
    scope: z.literal('genui_surface'),
    surface_id: safeString('surface_id'),
    schema_version: experimentVersionSchema,
    screen_id: safeString('screen_id'),
  })
  .strict();

export const genuiSurfaceElementTargetSchema = z
  .object({
    ...commonTargetFields,
    scope: z.literal('genui_surface_element'),
    surface_id: safeString('surface_id'),
    schema_version: experimentVersionSchema,
    screen_id: safeString('screen_id'),
    element_key: safeString('element_key'),
  })
  .strict();

export const experimentTargetSchema = z.discriminatedUnion('scope', [
  agentDefinitionTargetSchema,
  agentScreenElementTargetSchema,
  firstMessageTargetSchema,
  genuiFlowTargetSchema,
  genuiStepTargetSchema,
  genuiElementTargetSchema,
  genuiSurfaceTargetSchema,
  genuiSurfaceElementTargetSchema,
]);

export const experimentHypothesisSchema = z
  .object({
    id: safeString('hypothesis_id'),
    owner: safeString('hypothesis_owner'),
    statement: safeHumanText('hypothesis statement'),
    expected_direction: z.enum(['increase', 'decrease', 'neutral']),
    target_population: safeHumanText('target population'),
    decision_criteria: safeHumanText('decision criteria'),
  })
  .strict();

const experimentMetricEventPropertySchema = z
  .object({
    key: safeString('metric event-property key'),
    value: z.union([safeString('metric event-property value'), z.number().finite(), z.boolean()]),
  })
  .strict();

export const experimentMetricSchema = z
  .object({
    id: safeString('metric_id'),
    event: safeString('metric_event'),
    event_properties: z.array(experimentMetricEventPropertySchema).max(10).default([]),
    analysis_source: z
      .enum(['posthog_experiment', 'repository_readout'])
      .default('posthog_experiment'),
    aggregation: z.enum(['conversion_rate', 'count', 'mean', 'p50', 'p95']),
    denominator: safeString('metric_denominator'),
    expected_direction: z.enum(['increase', 'decrease', 'neutral']),
    exclusions: z.array(safeString('metric_exclusion')).max(10).default([]),
    window_days: z.number().int().positive().max(180),
    minimum_sample_size: z.number().int().positive().max(1_000_000),
  })
  .strict()
  .superRefine((metric, context) => {
    const keys = metric.event_properties.map(({ key }) => key);
    if (new Set(keys).size !== keys.length) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['event_properties'],
        message: 'metric event-property keys must be unique',
      });
    }
  });

export const experimentReadoutSchema = z
  .object({
    cadence: z.enum(['daily', 'weekly', 'monthly']),
    owner: safeString('owner'),
    primary_metric_id: safeString('primary_metric_id'),
    guardrail_metric_ids: z.array(safeString('guardrail_metric_id')).max(10).default([]),
    query_template: safeString('query_template'),
    exposure_compliance: z
      .object({
        denominator: safeString('exposure compliance denominator'),
        minimum_rate: z.number().min(0).max(1),
        breach_action: z.literal('block-decision'),
      })
      .strict()
      .optional(),
  })
  .strict();

export const experimentTargetingRuleSchema = z
  .object({
    enrollment_mode: z.enum(['operator-targeted', 'automatic']).default('operator-targeted'),
    rollout_percent: z.number().min(0).max(100).default(0),
    salt: safeString('salt'),
    platforms: z
      .array(z.enum(['ios', 'android', 'web', 'api']))
      .max(4)
      .optional(),
    locales: z
      .array(safeString('locale', /^[a-z]{2}(?:-[A-Z]{2})?$/))
      .max(20)
      .optional(),
    segment_keys: z.array(safeString('segment_key')).max(20).optional(),
    internal_cohorts: z.array(safeString('internal_cohort')).max(20).optional(),
    subject_overrides: z.record(experimentSubjectKeySchema, experimentVariantKeySchema).optional(),
    assignment_timing: z
      .object({
        trigger: safeString('assignment trigger'),
        member_day: z.number().int().min(0).max(180),
        deadline: safeString('assignment deadline'),
        missed_window: z.enum(['do-not-enroll', 'none']),
      })
      .strict()
      .optional(),
  })
  .strict();

export const experimentVariantSchema = z
  .object({
    key: experimentVariantKeySchema,
    label: safeHumanText('variant label'),
    allocation_percent: z.number().min(0).max(100),
    control: z.boolean().default(false),
    targets: z.array(experimentTargetSchema).min(1).max(8),
  })
  .strict();

export const experimentManifestSchema = z
  .object({
    experiment_key: experimentKeySchema,
    version: experimentVersionSchema,
    status: experimentStatusSchema,
    owner: safeString('owner'),
    assignment_source: experimentAssignmentSourceSchema,
    flag_key: experimentFlagKeySchema,
    target_set_policy: experimentTargetSetPolicySchema.default('same-scope'),
    hypothesis: experimentHypothesisSchema,
    primary_metric: experimentMetricSchema,
    guardrail_metrics: z.array(experimentMetricSchema).max(8).default([]),
    readout: experimentReadoutSchema,
    targeting: experimentTargetingRuleSchema,
    variants: z.array(experimentVariantSchema).min(2).max(12),
  })
  .strict()
  .superRefine((manifest, ctx) => {
    if (
      manifest.targeting.enrollment_mode === 'automatic' &&
      ((manifest.targeting.segment_keys?.length ?? 0) > 0 ||
        (manifest.targeting.internal_cohorts?.length ?? 0) > 0 ||
        Object.keys(manifest.targeting.subject_overrides ?? {}).length > 0)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['targeting'],
        message: 'automatic enrollment cannot use segment, cohort, or subject filters',
      });
    }

    if (
      manifest.targeting.assignment_timing &&
      manifest.targeting.enrollment_mode !== 'automatic'
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['targeting', 'assignment_timing'],
        message: 'assignment timing requires automatic enrollment',
      });
    }

    const primaryMetricIds = new Set([
      manifest.primary_metric.id,
      ...manifest.guardrail_metrics.map((metric) => metric.id),
    ]);
    if (!primaryMetricIds.has(manifest.readout.primary_metric_id)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['readout', 'primary_metric_id'],
        message: 'readout primary_metric_id must reference a manifest metric',
      });
    }

    const controlCount = manifest.variants.filter((variant) => variant.control).length;
    if (controlCount !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['variants'],
        message: 'exactly one variant must be marked control',
      });
    }

    const allocation = manifest.variants.reduce(
      (sum, variant) => sum + variant.allocation_percent,
      0,
    );
    if (Math.abs(allocation - 100) > 0.001) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['variants'],
        message: 'variant allocation_percent values must sum to 100',
      });
    }

    const signatureForPolicy = (target: ExperimentTarget): string =>
      manifest.target_set_policy === 'identical' ? targetSignature(target) : target.scope;
    const firstTargetSet = manifest.variants[0]?.targets.map(signatureForPolicy).sort();
    if (firstTargetSet) {
      const firstSignature = firstTargetSet.join('|');
      manifest.variants.forEach((variant, index) => {
        const signature = variant.targets.map(signatureForPolicy).sort().join('|');
        if (signature !== firstSignature) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['variants', index, 'targets'],
            message:
              manifest.target_set_policy === 'identical'
                ? 'coordinated experiment variants must expose the same target anchor set'
                : 'coordinated experiment variants must expose the same target scope set',
          });
        }
      });
    }
  });

export const experimentAssignmentSchema = z
  .object({
    assignment_id: safeString('assignment_id'),
    experiment_key: experimentKeySchema,
    experiment_version: experimentVersionSchema,
    assignment_source: experimentAssignmentSourceSchema,
    flag_key: experimentFlagKeySchema,
    variant_key: experimentVariantKeySchema,
    subject_key: experimentSubjectKeySchema,
    bucket: z.number().int().min(0).max(9999),
    assigned_at: z.string().datetime(),
    client_platform: z.enum(['ios', 'android', 'web', 'api']).optional(),
    locale: safeString('locale', /^[a-z]{2}(?:-[A-Z]{2})?$/).optional(),
    targets: z.array(experimentTargetSchema).min(1).max(8),
  })
  .strict();

export type ExperimentAssignmentSource = z.infer<typeof experimentAssignmentSourceSchema>;
export type ExperimentStatus = z.infer<typeof experimentStatusSchema>;
export type ExperimentSubjectKey = z.infer<typeof experimentSubjectKeySchema>;
export type ExperimentTargetScope = z.infer<typeof experimentTargetScopeSchema>;
export type ExperimentTargetSetPolicy = z.infer<typeof experimentTargetSetPolicySchema>;
export type ExperimentTarget = z.infer<typeof experimentTargetSchema>;
export type ExperimentHypothesis = z.infer<typeof experimentHypothesisSchema>;
export type ExperimentMetric = z.infer<typeof experimentMetricSchema>;
export type ExperimentReadout = z.infer<typeof experimentReadoutSchema>;
export type ExperimentTargetingRule = z.infer<typeof experimentTargetingRuleSchema>;
export type ExperimentVariant = z.infer<typeof experimentVariantSchema>;
export type ExperimentManifest = z.infer<typeof experimentManifestSchema>;
export type ExperimentAssignment = z.infer<typeof experimentAssignmentSchema>;

export const experimentStatusTransitions = {
  draft: ['ready'],
  ready: ['running'],
  running: ['paused', 'decided', 'archived'],
  paused: ['running', 'decided', 'archived'],
  decided: ['archived'],
  archived: [],
} as const satisfies Record<ExperimentStatus, readonly ExperimentStatus[]>;

export function canTransitionExperimentStatus(
  from: ExperimentStatus,
  to: ExperimentStatus,
): boolean {
  return experimentStatusTransitions[from].some((status) => status === to);
}

export function targetSignature(target: ExperimentTarget): string {
  return JSON.stringify(target);
}
